import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, LogOut, CheckCircle2, ExternalLink } from "lucide-react";
import { clearSession, getUser } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_RELAYRIGS_APP_URL;

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busyPlan, setBusyPlan] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => {});
    axios.get(`${API}/billing/me`).then((r) => setBilling(r.data)).catch(() => {});
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
    // Intentionally keyed on user?.id only — user is re-fetched inside this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user?.id]);

  const handleSubscribe = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan: planKey });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setBusyPlan(null);
    }
  };

  const handleCancel = async () => {
    setBusyPlan("cancel");
    try {
      await axios.post(`${API}/billing/cancel`, {});
      toast.success("Subscription cancelled.");
      setBilling((b) => ({ ...b, status: "cancelled" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "No active subscription to cancel.");
    } finally {
      setBusyPlan(null);
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    clearSession();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div data-testid="account-page" className="min-h-screen bg-rr-surface pt-28 pb-20 px-5">
      <div className="max-w-2xl mx-auto">
        <Link to="/" data-testid="account-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-rr-navy mb-8">
          <ArrowLeft size={16} /> Back to RelayRigs
        </Link>

        <div className="bg-white rounded-2xl p-8 border border-rr-line">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-rr-navy" data-testid="account-name">{user.name || user.email}</h1>
              <p className="text-rr-muted text-sm mt-1" data-testid="account-email">{user.email}</p>
              <span className="inline-block mt-2 text-xs font-bold bg-rr-surface px-3 py-1 rounded-full text-rr-navy capitalize">{user.role}</span>
            </div>
            <button onClick={handleLogout} data-testid="account-logout-button" className="flex items-center gap-2 text-sm font-bold text-rr-red">
              <LogOut size={16} /> Log Out
            </button>
          </div>

          {APP_URL && (
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-app-link"
              className="mt-6 flex items-center justify-between bg-rr-navy text-white rounded-xl px-5 py-4 hover:bg-rr-orange transition-colors"
            >
              <span className="font-bold text-sm">Open the RelayRigs App — jobs, wallet, HOS, documents & more</span>
              <ExternalLink size={18} />
            </a>
          )}
        </div>

        <div className="bg-white rounded-2xl p-8 border border-rr-line mt-6">
          <h2 className="font-heading font-bold text-lg text-rr-navy mb-4">Subscription</h2>
          {billing?.status === "active" ? (
            <div data-testid="account-active-plan" className="flex items-center justify-between bg-rr-green/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-rr-green font-bold text-sm">
                <CheckCircle2 size={18} /> Active — {billing.plan} plan
              </div>
              <button onClick={handleCancel} disabled={busyPlan === "cancel"} data-testid="account-cancel-button" className="text-sm font-bold text-rr-red">
                {busyPlan === "cancel" ? <Loader2 size={16} className="animate-spin" /> : "Unsubscribe"}
              </button>
            </div>
          ) : (
            <>
              <p data-testid="account-no-plan" className="text-sm text-rr-muted mb-5">You don't have an active RelayRigs Membership yet.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {plans.map((p) => (
                  <div key={p.key} data-testid={`account-plan-${p.key}`} className="border border-rr-line rounded-xl p-4 text-center">
                    <div className="font-heading font-bold text-rr-navy">{p.name}</div>
                    <div className="text-rr-orange font-bold mt-1">${p.amount}/mo</div>
                    <button onClick={() => handleSubscribe(p.key)} disabled={busyPlan === p.key} data-testid={`account-subscribe-${p.key}`} className="mt-3 w-full bg-rr-orange text-white text-xs font-bold py-2.5 rounded-full hover:bg-rr-navy transition-colors disabled:opacity-60">
                      {busyPlan === p.key ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Subscribe"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
